/**
 * ReprocessamentoAcompanhamentoPage — rota /reprocessamentos/:id (admin; ADR-036, Req 5.2–5.3, design §5): o
 * andamento (na fila, em execução, concluído, falha — com polling enquanto anda) e, quando já há visitas gravadas,
 * o resumo: quantas mudaram e quantas ficaram iguais, e as mudanças de marcador (antes → depois). Links para a lista
 * de visitas filtrada pelo reprocessamento ("Ver as N visitas" e "Ver só as que mudaram") e para as visitas de cada
 * lote do reprocessamento.
 *
 * A guarda de perfil (só admin) vive na rota (ProtectedRoute).
 */

import { Link, useParams } from 'react-router-dom';
import { useStatusReprocessamento } from '../hooks/usePainel';
import { MarcadorPill } from '../components/ConsultaShared';
import { formatarData } from '../components/format';
import {
  CLASSE_ANDAMENTO,
  ROTULO_ACAO,
  ROTULO_ANDAMENTO,
  descreverConjunto,
} from '../components/reprocessamentoTextos';

const numero = new Intl.NumberFormat('pt-BR');
const visitas = (n: number): string => `${numero.format(n)} ${n === 1 ? 'visita' : 'visitas'}`;

export function ReprocessamentoAcompanhamentoPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { status: r, carregando, erro } = useStatusReprocessamento(id || null);
  const base = `/visitas?reprocessamento=${encodeURIComponent(id)}`;

  return (
    <div>
      <p className="breadcrumb">
        <Link to="/reprocessamentos">Reprocessamentos</Link>
      </p>
      {carregando && <p className="muted">Carregando…</p>}
      {erro && <p className="notice">Não foi possível carregar o reprocessamento.</p>}

      {r && (
        <>
          <h1 className="page-title">{`Reprocessamento de ${formatarData(r.criadoEm)}`}</h1>

          <section className="card">
            <p>
              <span className={CLASSE_ANDAMENTO[r.status]}>{ROTULO_ANDAMENTO[r.status]}</span>{' '}
              {r.modo && <strong>{ROTULO_ACAO[r.modo]}</strong>}
            </p>
            {r.criterio !== undefined && <p>{descreverConjunto(r.criterio)}</p>}
            {r.disparadoPor && <p className="muted">{`Disparado por ${r.disparadoPor}`}</p>}
            <p data-testid="andamento">
              {`${numero.format(r.processadas)} de ${numero.format(r.afetadas)} visitas processadas`}
            </p>
            <div className="row-actions">
              <Link className="btn btn-ghost" to={base}>
                {r.afetadas === 1 ? 'Ver a visita' : `Ver as ${visitas(r.afetadas)}`}
              </Link>
              {(r.resumo?.mudaram ?? 0) > 0 && (
                <Link className="btn btn-ghost" to={`${base}&mudou=1`}>
                  Ver só as que mudaram
                </Link>
              )}
            </div>
          </section>

          {r.resumo && r.resumo.processadas > 0 && (
            <section className="card" data-testid="resumo-reprocessamento">
              <h2>Resumo</h2>
              <p>
                {`Das ${visitas(r.resumo.processadas)} processadas, ${numero.format(r.resumo.mudaram)} ${
                  r.resumo.mudaram === 1 ? 'mudou' : 'mudaram'
                } e ${numero.format(r.resumo.iguais)} ${r.resumo.iguais === 1 ? 'ficou igual' : 'ficaram iguais'}.`}
              </p>
              {r.resumo.transicoes.length > 0 && (
                <>
                  <h3>Mudanças de marcador</h3>
                  <ul className="lista-simples">
                    {r.resumo.transicoes.map((t) => (
                      <li key={`${t.antes ?? 'nova'}-${t.depois}`} data-testid={`transicao-${t.antes ?? 'nova'}-${t.depois}`}>
                        <MarcadorPill marcador={t.antes} /> → <MarcadorPill marcador={t.depois} /> {visitas(t.visitas)}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </section>
          )}

          {r.lotes && r.lotes.length > 0 && (
            <section className="card" data-testid="lotes-reprocessamento">
              <h2>Lotes</h2>
              <ul className="lista-simples">
                {r.lotes.map((l) => (
                  <li key={l.loteId}>
                    <span className="mono">{l.loteId.slice(0, 8)}</span>{' '}
                    <span className="muted">{`(${l.versaoPrompt})`}</span>{' '}
                    <Link to={`/visitas?lote=${encodeURIComponent(l.loteId)}`}>Ver as visitas do lote</Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}
    </div>
  );
}
